/**
 * Keeps `?lang=` on the URL through every in-app navigation.
 *
 * The shop puts the language on the iframe URL once. Inside the SPA many
 * places navigate with a fresh path (`/creator/<id>`, `/designs`, the step
 * links), which would drop the parameter. Reading the language then works
 * only through the value recorded at the iframe start, and a reload of a
 * URL without `lang` in a browser that blocks storage falls back to German.
 * This hook names the URL a lang-less location should be replaced with —
 * the last language the URL carried, kept in memory for the SPA session,
 * with the recorded iframe start as the fallback — so every seller URL
 * stays self-describing.
 *
 * The correction is decided at render time and rendered as a `<Navigate>`
 * INSTEAD of the routes, never fired from an effect next to them: React
 * runs child effects first, so a redirect a route issues on mount
 * (RoleDispatch at `/`, the `*` fallback) would be cancelled by a parent
 * effect navigating in the same commit and never retried, leaving a blank
 * page. Not rendering the routes until the URL is right avoids the race.
 *
 * Direct Sale keeps its own URL and never reads the recorded value; the
 * reference-colour areas (admin, login, onboarding, shop wizard)
 * are single-language.
 */

import { useEffect } from 'react';
import { useLocation, type Location } from 'react-router-dom';
import {
  isDirectSalePath,
  isReferencePath,
  readIframeParams,
} from './iframe-boot';

/** The raw `lang` value the URL last carried, for the life of the SPA session. */
let lastUrlLanguage: string | null = null;

/** Test seam: forgets the remembered language, as a page load would. */
export function resetRememberedLanguage(): void {
  lastUrlLanguage = null;
}

/**
 * The language a URL without `lang` should be given, or null when nothing
 * is known — a plain top-level visit stays as it is.
 */
export function languageToRestore(): string | null {
  return lastUrlLanguage ?? readIframeParams().lang ?? null;
}

function isLanguageNeutralPath(pathname: string): boolean {
  return isDirectSalePath(pathname) || isReferencePath(pathname);
}

/**
 * The location `location` should be replaced with so it carries `lang`, or
 * null when it is fine as it is.
 */
export function shopLanguageRedirect(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
): string | null {
  if (isLanguageNeutralPath(location.pathname)) return null;

  const params = new URLSearchParams(location.search);
  // An iframe start is the shop speaking; the boot redirect builds the
  // creator path itself, so there is nothing to add here.
  if (params.get('lang') || params.get('isIframe') === 'true') return null;

  const language = languageToRestore();
  if (!language) return null;

  params.set('lang', language);
  return `${location.pathname}?${params.toString()}${location.hash}`;
}

/**
 * Where the current location must be redirected to keep `lang`, or null.
 * Render a `<Navigate to={…} replace />` instead of the routes when set.
 * Also remembers the language the URL carries, so a later navigation that
 * drops it can be repaired; an iframe start replaces the memory with
 * whatever the shop sent (or omitted).
 */
export function useShopLanguageRedirect(): string | null {
  const location = useLocation();
  const redirect = shopLanguageRedirect(location);

  useEffect(() => {
    if (isLanguageNeutralPath(location.pathname)) return;
    const params = new URLSearchParams(location.search);
    const current = params.get('lang');
    if (current || params.get('isIframe') === 'true') {
      lastUrlLanguage = current;
    }
  }, [location]);

  return redirect;
}
